import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { useAuth } from "../contexts/AuthContext";
import {
  formatDate,
  formatCurrency,
  getDataSourceBadge,
} from "../utils/formatting";

import {
  buildSavedSingleGoogleMapsURL,
  buildSavedPairGoogleMapsURL,
} from "../utils/mapUtils";
import { useTheme } from "../contexts/ThemeContext";

// Helper function to get truck type label
const getTruckTypeLabel = (type) => {
  const truckTypes = {
    V: "Van",
    SV: "Box Truck",
    F: "Flatbed",
    R: "Reefer",
    SD: "Step Deck",
    DD: "Double Drop",
    LB: "Lowboy",
    RGN: "Removable Gooseneck",
    TNK: "Tanker",
    AC: "Auto Carrier",
    CONT: "Container",
    DT: "Dump Truck",
    HB: "Hopper Bottom",
    PO: "Power Only",
    HS: "Hot Shot",
  };
  return truckTypes[type] || type;
};

// Helper function to format distance
const getDistance = (load) => {
  if (load.distance) {
    return `${Math.round(load.distance).toLocaleString()} mi`;
  }
  if (load.length) {
    return `${Math.round(load.length).toLocaleString()} mi`;
  }
  return "Distance unknown";
};

const MyLoads = () => {
  const [bookedLoads, setBookedLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showOutbound, setShowOutbound] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchBookedLoads();

    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      fetchBookedLoads();
    }, 5000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [user, navigate]);

  useEffect(() => {
    const initialShowOutbound = bookedLoads.reduce((acc, load) => {
      acc[load.id] = true;
      return acc;
    }, {});
    setShowOutbound(initialShowOutbound);
  }, [bookedLoads]);

  const toggleLoadView = (loadId) => {
    setShowOutbound((prev) => ({
      ...prev,
      [loadId]: !prev[loadId],
    }));
  };

  const fetchBookedLoads = async () => {
    try {
      const response = await axios.get(`${API_URL}/booked-loads/assigned`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (response.status === 200) {
        setBookedLoads(response.data);
      } else {
        throw new Error("Failed to fetch booked loads");
      }
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const handleDeleteLoad = async (load) => {
    if (!window.confirm("Are you sure you want to delete this booked load?")) {
      return;
    }

    try {
      setDeletingId(load.id);
      const response = await axios.delete(
        `${API_URL}/booked-loads/${load.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      if (response.status === 200) {
        setBookedLoads((prevLoads) =>
          prevLoads.filter((l) => l.id !== load.id),
        );
      }
    } catch (error) {
      console.error("Error deleting load:", error);
      alert(
        error.response?.data?.detail ||
          "Failed to delete load. Please try again.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "50px 0" }}>
        <i
          className="fas fa-truck fa-spin"
          style={{
            fontSize: "3rem",
            color: "var(--primary)",
            marginBottom: "20px",
          }}
        ></i>
        <p>Loading your booked loads...</p>
      </div>
    );

  if (error)
    return (
      <div
        style={{
          color: "var(--danger)",
          padding: "20px",
          border: "1px solid var(--danger)",
          borderRadius: "8px",
          textAlign: "center",
        }}
      >
        <i
          className="fas fa-exclamation-triangle"
          style={{ fontSize: "2rem", marginBottom: "10px" }}
        ></i>
        <p>{error}</p>
      </div>
    );
  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <i
            className="fas fa-calendar-check"
            style={{ fontSize: "2rem", color: "var(--secondary)" }}
          ></i>
          <h2>BOOKED LOADS</h2>
        </div>
      </div>

      <p style={{ color: "var(--text)", marginBottom: "20px" }}>
        <i className="fas fa-info-circle" style={{ marginRight: "8px" }}></i>
        You have {bookedLoads.length} booked load(s)
      </p>

      {bookedLoads.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "20px",
            backgroundColor: "var(--surface)",
            borderRadius: "8px",
            color: "var(--text-muted)",
          }}
        >
          No booked loads
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
            gap: "20px",
          }}
        >
          {bookedLoads.map((load) => (
            <div
              key={load.id}
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "8px",
                boxShadow: "var(--shadow)",
                overflow: "hidden",
                border: "1px solid var(--chrome)",
              }}
            >
              {/* Header with route info */}
              <div
                style={{
                  backgroundColor: load.is_pair
                    ? "var(--accent-purple)"
                    : load.green_light
                      ? "var(--accent-green)"
                      : "#2196f3",
                  color: "var(--text-inverse)",
                  padding: "15px",
                  position: "relative",
                }}
              >
                {/* API Source Badge */}
                <div
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    backgroundColor: "none",
                    color: "white",
                  }}
                >
                  {getDataSourceBadge(load.data_source, theme)}
                </div>

                <div style={{ marginBottom: "5px" }}>
                  <h3
                    style={{
                      margin: "0",
                      fontSize: "18px",
                      paddingRight: "100px",
                      cursor: "pointer",
                    }}
                  >
                    {!load.is_pair ? (
                      <>
                        {load.origin_city}, {load.origin_state} →{" "}
                        {load.destination_city}, {load.destination_state}
                      </>
                    ) : showOutbound[load.id] !== false ? (
                      <>
                        {load.origin_city}, {load.origin_state} →{" "}
                        {load.destination_city}, {load.destination_state}
                      </>
                    ) : (
                      <>
                        {load.child.origin_city}, {load.child.origin_state} →{" "}
                        {load.child.destination_city},{" "}
                        {load.child.destination_state}
                      </>
                    )}
                  </h3>
                </div>

                <div
                  style={{
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                  }}
                >
                  <span>
                    <i
                      className="far fa-calendar-alt"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Ship Date: {formatDate(load.ship_date)}
                  </span>
                  {load.is_pair ? (
                    <>
                      <span>
                        <i
                          className="fas fa-route"
                          style={{ marginRight: "4px" }}
                        ></i>
                        {(
                          (load.distance || 0) +
                          (load.child?.distance || 0) +
                          (load.dead_head || 0)
                        ).toLocaleString()}{" "}
                        mi
                      </span>
                      <span>
                        <i
                          className="fas fa-dollar-sign"
                          style={{ marginRight: "4px" }}
                        ></i>
                        {(
                          (load.rate_per_mile_est || 0) * (load.distance || 0) +
                          (load.child?.rate_per_mile_est || 0) *
                            (load.child?.distance || 0)
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </>
                  ) : (
                    <>
                      <span>
                        <i
                          className="fas fa-route"
                          style={{ marginRight: "4px" }}
                        ></i>
                        {getDistance(load)}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Load details */}
              <div style={{ padding: "15px", color: "var(--text)" }}>
                {load.is_pair && (
                  <div
                    style={{
                      marginBottom: "15px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: "1px solid var(--chrome)",
                      paddingBottom: "10px",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "0.9rem",
                        fontWeight: "500",
                      }}
                    >
                      {showOutbound[load.id] ? "Outbound Load" : "Return Load"}
                    </span>
                    <button
                      onClick={() => toggleLoadView(load.id)}
                      style={{
                        backgroundColor: "var(--accent-purple)",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: "0.9rem",
                        cursor: "pointer",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <i className="fas fa-exchange-alt"></i>
                      Show {showOutbound[load.id] ? "Return" : "Outbound"} Load
                    </button>
                  </div>
                )}
                <div style={{ display: "flex", gap: "16px" }}>
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        margin: "0 0 6px 0",
                        color: "var(--text-secondary)",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                      }}
                    >
                      LOAD DETAILS
                    </h4>
                    {(() => {
                      const currentLoad =
                        showOutbound[load.id] !== false
                          ? load
                          : load.child || load;
                      return (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            fontSize: "0.85rem",
                          }}
                        >
                          <div>
                            <strong>Equip:</strong>{" "}
                            {getTruckTypeLabel(currentLoad.equipment_type)}
                          </div>
                          <div>
                            <strong>Weight:</strong>{" "}
                            {currentLoad.weight?.toLocaleString() || 0}
                          </div>
                          <div>
                            <strong>Rate:</strong> $
                            {currentLoad.rate_per_mile_est?.toFixed(2) ||
                              "0.00"}
                            /mi
                          </div>
                          <div>
                            <strong>Distance:</strong>{" "}
                            {getDistance(currentLoad)}
                          </div>
                          <div>
                            <strong>Total:</strong>{" "}
                            {formatCurrency(
                              currentLoad.rate_per_mile_est *
                                currentLoad.distance,
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        margin: "0 0 6px 0",
                        color: "var(--text-secondary)",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                      }}
                    >
                      BOOKING INFO
                    </h4>
                    {(() => {
                      return (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            fontSize: "0.85rem",
                          }}
                        >
                          <div>
                            <strong>Status:</strong>{" "}
                            <span
                              style={{
                                color: load.green_light
                                  ? "var(--accent-green)"
                                  : "var(--warning)",
                                fontWeight: "500",
                              }}
                            >
                              {load.green_light ? "Ready to Go" : "Pending"}
                            </span>
                          </div>
                          {(() => {
                            const currentLoad =
                              showOutbound[load.id] !== false
                                ? load
                                : load.child || load;
                            return (
                              currentLoad.phone_number && (
                                <div>
                                  <strong>Phone:</strong>{" "}
                                  <a href={`tel:${currentLoad.phone_number}`}>
                                    {currentLoad.phone_number}
                                  </a>
                                </div>
                              )
                            );
                          })()}
                          <div>
                            <strong>Booked On:</strong>{" "}
                            {formatDate(load.booking_date)}
                          </div>
                          {user?.account_type === "dispatcher" ? (
                            load.load_for && (
                              <div>
                                <strong>Booked For:</strong> {load.load_for}
                              </div>
                            )
                          ) : (
                            <div>
                              <strong>Booked By:</strong>{" "}
                              {load.booked_by?.split("@")[0]}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div
                style={{
                  padding: "10px",
                  borderTop: "1px solid var(--chrome)",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "10px",
                  backgroundColor: "none",
                }}
              >
                <a
                  href={
                    load.is_pair
                      ? buildSavedPairGoogleMapsURL(load, load.child)
                      : buildSavedSingleGoogleMapsURL(load)
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    backgroundColor: "var(--accent-blue)",
                    color: "white",
                    border: "none",
                    padding: "12px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    textDecoration: "none",
                    lineHeight: "1.2",
                  }}
                >
                  <i className="fas fa-map-marked-alt"></i>
                  VIEW ROUTE
                </a>

                <button
                  onClick={() => handleDeleteLoad(load)}
                  disabled={deletingId === load.id}
                  style={{
                    backgroundColor: "var(--danger)",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: deletingId === load.id ? "wait" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {deletingId === load.id ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyLoads;
